import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import whatsappService, { QueueLogStats, WhatsappQueueItem } from '../services/whatsappService';

const TEMPLATE_LABELS: Record<string, string> = {
  bill_reminder: 'Reminder Tagihan',
  payment_confirmed: 'Konfirmasi Bayar',
  payment_created: 'Tagihan Baru',
  overdue_notice: 'Tunggakan',
  reminder_7_days: 'Reminder H-7',
  reminder_3_days: 'Reminder H-3',
  reminder_1_day: 'Reminder H-1',
  reminder_due_today: 'Jatuh Tempo',
  broadcast: 'Broadcast',
  custom: 'Custom',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Menunggu',
  sent: 'Terkirim',
  failed: 'Gagal',
  cancelled: 'Dibatalkan',
};

const WhatsappQueueLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [queue, setQueue] = useState<WhatsappQueueItem[]>([]);
  const [stats, setStats] = useState<QueueLogStats | null>(null);
  const [summary, setSummary] = useState<Record<string, number>>({});

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');

  const [selectedMessage, setSelectedMessage] = useState<WhatsappQueueItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: {
        page: number;
        limit: number;
        status?: string;
        template_type?: string;
      } = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (templateFilter !== 'all') params.template_type = templateFilter;

      const response = await whatsappService.getQueueLog(params);
      setQueue(response.queue);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
      setSummary(response.summary);
    } catch (err) {
      console.error('Error loading queue log:', err);
      setError('Gagal memuat log antrian WhatsApp');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, templateFilter]);

  const loadStats = async () => {
    try {
      const statsData = await whatsappService.getQueueLogStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading queue stats:', err);
    }
  };

  useEffect(() => {
    loadQueue();
    loadStats();
  }, [loadQueue]);

  const handleRefresh = () => {
    loadQueue();
    loadStats();
  };

  const handleProcessQueue = async () => {
    try {
      setProcessing(true);
      setError(null);
      const result = await whatsappService.processQueue();
      const processed = result.data?.processed ?? result.processed ?? 0;
      setSuccess(`Antrian diproses. ${processed} pesan dikirim ke StarSender.`);
      handleRefresh();
    } catch (err: any) {
      console.error('Error processing queue:', err);
      setError(err.response?.data?.message || 'Gagal memproses antrian');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelMessage = async (id: number) => {
    if (!window.confirm('Batalkan pesan ini dari antrian?')) return;

    try {
      await whatsappService.cancelQueueMessage(id);
      setSuccess('Pesan berhasil dibatalkan');
      handleRefresh();
    } catch (err: any) {
      console.error('Error cancelling queue message:', err);
      setError(err.response?.data?.message || 'Gagal membatalkan pesan');
    }
  };

  const handleViewMessage = (item: WhatsappQueueItem) => {
    setSelectedMessage(item);
    setDialogOpen(true);
  };

  const toggleRowExpansion = (rowId: number) => {
    const next = new Set(expandedRows);
    if (next.has(rowId)) next.delete(rowId);
    else next.add(rowId);
    setExpandedRows(next);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon fontSize="small" />;
      case 'pending':
        return <ScheduleIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const formatTarget = (item: WhatsappQueueItem) => {
    if (item.group_id) return `Grup: ${item.group_id}`;
    if (item.phone) return item.phone;
    return '-';
  };

  const formatTemplateType = (type: string | null) => {
    if (!type) return '-';
    return TEMPLATE_LABELS[type] || type;
  };

  const filteredQueue = queue.filter((item) => {
    if (!searchFilter.trim()) return true;
    const q = searchFilter.toLowerCase();
    return (
      (item.phone || '').includes(searchFilter) ||
      (item.group_id || '').toLowerCase().includes(q) ||
      item.message.toLowerCase().includes(q) ||
      (item.template_type || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Log Antrian WhatsApp
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Riwayat semua pesan dalam antrian — reminder tagihan, konfirmasi, broadcast, dan lainnya.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleProcessQueue}
          disabled={processing || loading}
        >
          Proses Antrian
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {(stats || summary) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Menunggu
              </Typography>
              <Typography variant="h5" color="warning.main">
                {(stats?.totalPending ?? summary.pending ?? 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Terkirim
              </Typography>
              <Typography variant="h5" color="success.main">
                {(stats?.totalSent ?? summary.sent ?? 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Gagal
              </Typography>
              <Typography variant="h5" color="error.main">
                {(stats?.totalFailed ?? summary.failed ?? 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Dibatalkan
              </Typography>
              <Typography variant="h5">{(summary.cancelled ?? 0).toLocaleString()}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Terkirim (24 jam)
              </Typography>
              <Typography variant="h5">{(stats?.sentLastDay ?? 0).toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 2,
              alignItems: 'center',
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
                label="Status"
              >
                <MenuItem value="all">Semua Status</MenuItem>
                <MenuItem value="pending">Menunggu</MenuItem>
                <MenuItem value="sent">Terkirim</MenuItem>
                <MenuItem value="failed">Gagal</MenuItem>
                <MenuItem value="cancelled">Dibatalkan</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Jenis</InputLabel>
              <Select
                value={templateFilter}
                onChange={(e) => {
                  setPage(1);
                  setTemplateFilter(e.target.value);
                }}
                label="Jenis"
              >
                <MenuItem value="all">Semua Jenis</MenuItem>
                {Object.entries(TEMPLATE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Cari tujuan/pesan"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Nomor, grup, atau kata kunci pesan"
            />
            <Button
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
              fullWidth
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total {total.toLocaleString()} entri
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={48} />
                      <TableCell>Tujuan</TableCell>
                      <TableCell>Jenis</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Prioritas</TableCell>
                      <TableCell>Dijadwalkan</TableCell>
                      <TableCell>Dikirim</TableCell>
                      <TableCell>Retry</TableCell>
                      <TableCell width={100}>Aksi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredQueue.map((item) => (
                      <React.Fragment key={item.id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleRowExpansion(item.id)}>
                              {expandedRows.has(item.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>{formatTarget(item)}</TableCell>
                          <TableCell>{formatTemplateType(item.template_type)}</TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(item.status)}
                              label={STATUS_LABELS[item.status] || item.status}
                              color={getStatusColor(item.status) as 'success' | 'warning' | 'error' | 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={`P${item.priority}`} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {format(new Date(item.scheduled_at), 'dd/MM/yy HH:mm', { locale: id })}
                          </TableCell>
                          <TableCell>
                            {item.sent_at
                              ? format(new Date(item.sent_at), 'dd/MM/yy HH:mm', { locale: id })
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {item.retry_count} / {item.max_retries || 3}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Lihat pesan">
                                <IconButton size="small" onClick={() => handleViewMessage(item)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {item.status === 'pending' && (
                                <Tooltip title="Batalkan">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleCancelMessage(item.id)}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0 }}>
                            <Collapse in={expandedRows.has(item.id)} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Isi Pesan
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {item.message}
                                  </Typography>
                                </Paper>
                                {item.error_message && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="subtitle2" color="error" gutterBottom>
                                      Error
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
                                      <Typography variant="body2" color="error">
                                        {item.error_message}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  Dibuat:{' '}
                                  {format(new Date(item.createdAt), 'dd MMMM yyyy, HH:mm', { locale: id })}
                                  {item.related_id ? ` | ID terkait: ${item.related_id}` : ''}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}

              {filteredQueue.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Tidak ada log antrian ditemukan
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detail Pesan Antrian</DialogTitle>
        <DialogContent dividers>
          {selectedMessage && (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2">Tujuan</Typography>
                  <Typography variant="body1">{formatTarget(selectedMessage)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Jenis</Typography>
                  <Typography variant="body1">
                    {formatTemplateType(selectedMessage.template_type)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedMessage.status)}
                    label={STATUS_LABELS[selectedMessage.status] || selectedMessage.status}
                    color={getStatusColor(selectedMessage.status) as 'success' | 'warning' | 'error' | 'default'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2">Prioritas</Typography>
                  <Typography variant="body1">Level {selectedMessage.priority}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Dijadwalkan</Typography>
                  <Typography variant="body1">
                    {format(new Date(selectedMessage.scheduled_at), 'dd MMMM yyyy, HH:mm', { locale: id })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Dikirim</Typography>
                  <Typography variant="body1">
                    {selectedMessage.sent_at
                      ? format(new Date(selectedMessage.sent_at), 'dd MMMM yyyy, HH:mm', { locale: id })
                      : 'Belum dikirim'}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Isi Pesan
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.message}
                </Typography>
              </Paper>

              {selectedMessage.error_message && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Pesan Error
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
                    <Typography variant="body2" color="error">
                      {selectedMessage.error_message}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsappQueueLog;
